import {computed, type ComputedRef, type InjectionKey, ref, type Ref} from 'vue';

/** Reactive consent selection state shared by the consent form and the stepper footer. */
export interface ConsentSelection {
  termsAccepted: Ref<boolean>;
  appUseAccepted: Ref<boolean>;
  productImprovement: Ref<boolean>;
  analyticsAnonymized: Ref<boolean>;
  research: Ref<boolean>;
  optionalExpanded: Ref<boolean>;
  canContinue: ComputedRef<boolean>;
  isEssentialsOnlyActive: ComputedRef<boolean>;
  isAcceptAllActive: ComputedRef<boolean>;
  acceptAll(): void;
  essentialsOnly(): void;
  toggleOptional(): void;
  reset(): void;
  revealOptionalIfSelected(): void;
}

/** Injection key for providing the consent selection from the sign-up page to the consent form. */
export const consentSelectionKey: InjectionKey<ConsentSelection> = Symbol('consentSelection');

/**
 * Drives the registration consent screen (IPD-2700).
 *
 * Tracks the two required and three optional consents. The consent step shows a
 * persistent footer (Essentials Only + Accept All on top, Back + Continue
 * below); `canContinue` gates the Continue button on both required consents
 * being ticked, while optional consents never affect it.
 *
 * `acceptAll` / `essentialsOnly` only set the boxes (and expand the optional
 * group so the user can see what they accept); they intentionally do not
 * advance the flow — advancing requires the explicit Continue click (GDPR
 * two-click requirement).
 *
 * `reset` returns the screen to its first-time state; the sign-up page calls it
 * when the user re-enters the consent step so the selection is not carried over.
 */
export function useConsentSelection(): ConsentSelection {
  const termsAccepted = ref(false);
  const appUseAccepted = ref(false);
  const productImprovement = ref(false);
  const analyticsAnonymized = ref(false);
  const research = ref(false);
  const optionalExpanded = ref(false);

  const canContinue = computed(() => termsAccepted.value && appUseAccepted.value);

  // True when the current state exactly matches the "essentials only" selection
  // (required boxes checked, all optional unchecked) — used to disable that CTA.
  const isEssentialsOnlyActive = computed(() =>
    termsAccepted.value && appUseAccepted.value &&
    !productImprovement.value && !analyticsAnonymized.value && !research.value);

  // True when every consent is checked — used to disable the "Accept All" CTA.
  const isAcceptAllActive = computed(() =>
    termsAccepted.value && appUseAccepted.value &&
    productImprovement.value && analyticsAnonymized.value && research.value);

  function acceptAll(): void {
    termsAccepted.value = true;
    appUseAccepted.value = true;
    productImprovement.value = true;
    analyticsAnonymized.value = true;
    research.value = true;
    optionalExpanded.value = true;
  }

  function essentialsOnly(): void {
    termsAccepted.value = true;
    appUseAccepted.value = true;
    productImprovement.value = false;
    analyticsAnonymized.value = false;
    research.value = false;
    optionalExpanded.value = true;
  }

  function toggleOptional(): void {
    optionalExpanded.value = !optionalExpanded.value;
  }

  function reset(): void {
    termsAccepted.value = false;
    appUseAccepted.value = false;
    productImprovement.value = false;
    analyticsAnonymized.value = false;
    research.value = false;
    optionalExpanded.value = false;
  }

  // Expand the optional group if any optional consent is ticked, so a ticked
  // consent is never hidden (used when returning to the step from user data).
  function revealOptionalIfSelected(): void {
    if (productImprovement.value || analyticsAnonymized.value || research.value) {
      optionalExpanded.value = true;
    }
  }

  return {
    termsAccepted,
    appUseAccepted,
    productImprovement,
    analyticsAnonymized,
    research,
    optionalExpanded,
    canContinue,
    isEssentialsOnlyActive,
    isAcceptAllActive,
    acceptAll,
    essentialsOnly,
    toggleOptional,
    reset,
    revealOptionalIfSelected,
  };
}
